import {
  handleUrl,
  myHeadersWithToken,
  myHeadersWithTokenContentType,
} from '../global';
import { requestJson } from '../http';
import {
  CreateWorkOrderProps,
  DetailProps,
  GetAllWorkOrderProps,
  GetArchivedWorkOrdersProps,
  GetInStatusWorkOrdersProps,
  GetPipelineFiltersProps,
  GetReportGraphsProps,
  GetTopWorkOrdersProps,
  GetWorkOrderProps,
  GetWorkOrdersListProps,
  GetWorkOrdersParams,
  ReportPdfLink,
} from './types';

export const createWorkOrder = (
  data: FormData
): Promise<CreateWorkOrderProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return requestJson(`${baseUrl}/api/V1/work-orders`, requestOptions);
};

export const editWorkOrder = (
  id: number,
  data: FormData,
  updateCoordinate = false
): Promise<CreateWorkOrderProps> => {
  const requestOptions = {
    method: 'PUT',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}?force_coordinate_event=${updateCoordinate}`,
    requestOptions
  );
};

export const getArchivedWorkOrders = (
  page: number,
  size: number,
  search: string
): Promise<GetArchivedWorkOrdersProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
    search_string: search,
  });
  return requestJson(
    `${baseUrl}/api/V1/work-orders/archived?${query.toString()}`,
    requestOptions
  );
};

export const getPipelineFilters = (
  params: GetWorkOrdersParams,
  signal?: AbortSignal
): Promise<GetPipelineFiltersProps> => {
  const { uf, city, licenseCompanyId } = params;
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  const query = new URLSearchParams();
  if (uf) query.set('uf', String(uf));
  if (city) query.set('city_name', city);
  if (licenseCompanyId)
    query.set('worker_company_id', String(licenseCompanyId));

  return requestJson(
    `${baseUrl}/api/V1/work-orders/filters?${query.toString()}`,
    requestOptions,
    signal
  );
};

export const getAllWorkOrders = (
  params: GetWorkOrdersParams,
  signal?: AbortSignal
): Promise<GetAllWorkOrderProps> => {
  const { search, uf, city, licenseCompanyId, startDate, endDate } = params;
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  const query = new URLSearchParams();
  if (search) query.set('search_string', search);
  if (uf) query.set('uf', String(uf));
  if (city) query.set('city_name', city);
  if (licenseCompanyId)
    query.set('worker_company_id', String(licenseCompanyId));
  if (startDate) query.set('start_date', startDate);
  if (endDate) query.set('end_date', endDate);

  return requestJson(
    `${baseUrl}/api/V1/work-orders?${query.toString()}`,
    requestOptions,
    signal
  );
};

/** Paginated flat list (`/work-orders/list`): search only, no pipeline filters. */
export const getWorkOrdersList = (
  page: number,
  size: number,
  search: string,
  signal?: AbortSignal
): Promise<GetWorkOrdersListProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  const query = new URLSearchParams({ page: String(page), size: String(size) });
  if (search) query.set('search_string', search);
  return requestJson(
    `${baseUrl}/api/V1/work-orders/list?${query.toString()}`,
    requestOptions,
    signal
  );
};

export const getTopWorkOrders = (
  signal?: AbortSignal
): Promise<GetTopWorkOrdersProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/top`,
    requestOptions,
    signal
  );
};

export const getWorkOrdersInStatus = (
  status: number,
  page: number,
  size: number,
  sortType: 'asc' | 'desc'
): Promise<GetInStatusWorkOrdersProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/in-status/${status}?page=${page}&size=${size}&sort=${sortType}`,
    requestOptions
  );
};

export const GetWorkOrder = (id: number): Promise<GetWorkOrderProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return requestJson(`${baseUrl}/api/V1/work-orders/${id}`, requestOptions);
};

export const CancelWorkOrder = (id: number): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PUT',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/cancel`,
    requestOptions
  );
};

export const ChangeStatus = (id: number): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PUT',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/status`,
    requestOptions
  );
};

export const PreviousStatus = (id: number): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PUT',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/previous-status`,
    requestOptions
  );
};

export const getReportPDF = (id: number): Promise<Uint8Array> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/work-orders/${id}/report-pdf?inline=false`,
    requestOptions
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error('Não foi possível carregar o relatório');
      }
      return response.arrayBuffer();
    })
    .then((buffer) => new Uint8Array(buffer))
    .catch((error) => {
      throw new Error(error.message);
    });
};

export const getReportPDFLink = (id: number): Promise<ReportPdfLink> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/report-pdf?inline=true`,
    requestOptions
  );
};

export const sendGraphs = (
  id: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/report-graphs`,
    requestOptions
  );
};

export const UploadFacadeImage = (
  id: number,
  data: FormData
): Promise<DetailProps> => {
  const requestOptions = {
    method: 'PUT',
    headers: myHeadersWithToken(),
    body: data,
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/facade-image`,
    requestOptions
  );
};

export const exportSample = (id: number): Promise<Response> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/work-orders/${id}/samples-xlsx`,
    requestOptions
  ).then((response) => response);
};

export const getReportGraphs = (id: number): Promise<GetReportGraphsProps> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithToken(),
  };
  const baseUrl = handleUrl();
  return requestJson(
    `${baseUrl}/api/V1/work-orders/${id}/report-graphs`,
    requestOptions
  );
};
